import { useCallback } from 'react';
import { useChatInstancesStore } from '../store/chatInstancesStore';
import { useLayoutStore } from '../store/layoutStore';

export function useDetachedWindows() {
  const markTabDetached = useLayoutStore((state) => state.markTabDetached);
  const markTabReattached = useLayoutStore((state) => state.markTabReattached);
  const markCopilotDetached = useLayoutStore(
    (state) => state.markCopilotDetached,
  );
  const markCopilotReattached = useLayoutStore(
    (state) => state.markCopilotReattached,
  );
  const setActiveMainContentTab = useLayoutStore(
    (state) => state.setActiveMainContentTab,
  );
  const detachedTabIds = useLayoutStore((state) => state.detachedTabIds);
  const isCopilotDetached = useLayoutStore((state) => state.isCopilotDetached);

  const activateFallbackTab = useCallback(
    (detachedTabIdsInput: string | string[]) => {
      const detachedBatch = Array.isArray(detachedTabIdsInput)
        ? detachedTabIdsInput
        : [detachedTabIdsInput];
      const {
        activeMainContentTabId: currentActiveTabId,
        tabHistory: currentTabHistory,
        detachedTabIds: currentDetachedTabIds,
        setActiveMainContentTab: activateTab,
      } = useLayoutStore.getState();

      const excludedTabIds = new Set([
        ...currentDetachedTabIds,
        ...detachedBatch,
      ]);

      if (!currentActiveTabId || !excludedTabIds.has(currentActiveTabId)) {
        return;
      }

      const fallbackTabId = currentTabHistory.find(
        (tabId) => !excludedTabIds.has(tabId),
      );

      if (fallbackTabId) {
        activateTab(fallbackTabId);
      }
    },
    [],
  );

  const detachTabs = useCallback(
    async (tabIds: string[]) => {
      if (!window.electronAPI?.detachedWindows || tabIds.length === 0) {
        return;
      }

      const successfullyDetached: string[] = [];

      for (const tabId of tabIds) {
        const result = await window.electronAPI.detachedWindows.detachTab({
          tabId,
        });

        if (!result.success) {
          console.warn('Failed to detach tab to new window', {
            tabId,
            error: result.error,
          });
          continue;
        }

        successfullyDetached.push(tabId);
        markTabDetached(tabId);
      }

      if (successfullyDetached.length > 0) {
        activateFallbackTab(successfullyDetached);
      }
    },
    [activateFallbackTab, markTabDetached],
  );

  const detachTab = useCallback(
    async (tabId: string) => {
      await detachTabs([tabId]);
    },
    [detachTabs],
  );

  const reattachTab = useCallback(
    async (tabId: string) => {
      if (!window.electronAPI?.detachedWindows) {
        return;
      }

      const result = await window.electronAPI.detachedWindows.reattachTab({
        tabId,
      });

      if (!result.success) {
        console.warn('Failed to reattach tab from detached window', { tabId });

        return;
      }

      markTabReattached(tabId);
      setActiveMainContentTab(tabId);
    },
    [markTabReattached, setActiveMainContentTab],
  );

  const detachCopilot = useCallback(async () => {
    if (!window.electronAPI?.detachedWindows) {
      return;
    }

    const result = await window.electronAPI.detachedWindows.detachCopilot({});

    if (!result.success) {
      console.warn('Failed to detach co-pilot to new window', {
        error: result.error,
      });

      return;
    }

    markCopilotDetached();
    useChatInstancesStore.getState().discardInMemoryChats();
  }, [markCopilotDetached]);

  const reattachCopilot = useCallback(async () => {
    if (!window.electronAPI?.detachedWindows) {
      return;
    }

    const result = await window.electronAPI.detachedWindows.reattachCopilot();

    if (!result.success) {
      console.warn('Failed to reattach co-pilot from detached window');

      return;
    }

    useChatInstancesStore.getState().discardInMemoryChats();
    markCopilotReattached();
  }, [markCopilotReattached]);

  const revealMainAlongsideCopilot = useCallback(async () => {
    if (!window.electronAPI?.detachedWindows?.tileCopilotWithMain) {
      return;
    }

    const result =
      await window.electronAPI.detachedWindows.tileCopilotWithMain();

    if (!result.success) {
      console.warn('Failed to tile co-pilot alongside main window', {
        error: result.error,
      });
    }
  }, []);

  return {
    detachedTabIds,
    isCopilotDetached,
    detachTab,
    detachTabs,
    reattachTab,
    detachCopilot,
    reattachCopilot,
    revealMainAlongsideCopilot,
  };
}
